import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Check, Loader2 } from "lucide-react";
import { doorsApi, buildingsApi, labelTemplateApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const RED = "#ED1C24";

// Solid-colour logo silhouette (CSS mask) so a single-colour thermal head
// renders it in one ink — red on M421 red/black paper, else black.
function LogoMark({ src, color, style }) {
  return (
    <div
      style={{
        ...style,
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

function LabelCard({ door, buildingName, tpl }) {
  const brand = tpl.brand_text || tpl.heading || "";
  const redBlack = (tpl.color_mode || "mono") === "red_black";
  const wMm = Math.max(20, Number(tpl.label_width_mm) || 50);
  const hMm = Math.max(20, Number(tpl.label_height_mm) || 30);
  const isPortrait = (tpl.orientation || "landscape") === "portrait";
  // The media is always landscape (wMm × hMm); portrait rotates content.
  return (
    <div
      className="label-card relative flex items-center justify-center overflow-hidden rounded-lg border border-slate-300 bg-white"
      style={{ width: `${wMm}mm`, height: `${hMm}mm` }}
    >
      <div
        className="flex flex-col items-center justify-center gap-1 text-center"
        style={isPortrait ? { transform: "rotate(90deg)", width: `${hMm}mm`, height: `${wMm}mm` } : { width: "100%" }}
      >
        {tpl.label_logo_base64 ? (
          <LogoMark src={tpl.label_logo_base64} color={redBlack ? RED : "#000"} style={{ width: "60%", height: `${Math.max(4, Math.round((isPortrait ? wMm : hMm) * 0.14))}mm` }} />
        ) : brand ? (
          <p className="text-xs font-bold uppercase tracking-wide text-slate-700">{brand}</p>
        ) : null}
        <QRCodeSVG value={`fdcheck://door/${door.id}`} size={110} level="M" includeMargin />
        <p className="text-base font-extrabold leading-tight text-slate-900">{door.door_reference || "Door"}</p>
        {door.location ? <p className="text-[10px] text-slate-500">{door.location}</p> : null}
        {buildingName ? <p className="text-[10px] text-slate-400">{buildingName}</p> : null}
        {tpl.extra_line ? <p className="text-[10px] text-slate-400">{tpl.extra_line}</p> : null}
      </div>
    </div>
  );
}

export default function LabelSheet({ mode }) {
  const { id } = useParams();
  const tpl = useAsync(() => labelTemplateApi.get().catch(() => ({ data: {} })), []);
  const door = useAsync(() => (mode === "door" ? doorsApi.getOne(id) : Promise.resolve({ data: null })), [id, mode]);
  const building = useAsync(() => buildingsApi.getOne(mode === "door" ? undefined : id).catch(() => ({ data: null })), [id, mode]);
  const buildingIdForDoors = mode === "building" ? id : null;
  const doors = useAsync(() => (buildingIdForDoors ? doorsApi.getAll(buildingIdForDoors) : Promise.resolve({ data: [] })), [buildingIdForDoors]);
  const [onlyUnprinted, setOnlyUnprinted] = useState(false);
  const [busy, setBusy] = useState(false);

  const template = tpl.data || {};
  const loading = tpl.loading || door.loading || doors.loading;

  const buildingName = mode === "door"
    ? (door.data?.building_name || "")
    : (building.data?.name || "");

  const labelDoors = useMemo(() => {
    if (mode === "door") return door.data ? [door.data] : [];
    let arr = doors.data || [];
    if (onlyUnprinted) arr = arr.filter((d) => !d.label_printed_at && !d.labels_printed);
    return arr;
  }, [mode, door.data, doors.data, onlyUnprinted]);

  const markPrinted = async () => {
    const ids = labelDoors.map((d) => d.id);
    if (!ids.length) return;
    setBusy(true);
    try {
      await doorsApi.markLabelsPrinted(ids);
      toast.success(`Marked ${ids.length} label${ids.length > 1 ? "s" : ""} as printed`);
      if (mode === "building") doors.reload();
    } catch (e) { toast.error(apiError(e, "Could not update")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <style>{`
        .label-card, .label-card * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          .label-sheet { gap: 6mm; }
        }
      `}</style>
      <div className="no-print">
        <PageHeader
          title="QR door labels"
          subtitle={mode === "door" ? "Printable label for this door" : buildingName || "Printable labels for this building"}
          testId="label-sheet-header"
          actions={
            <div className="flex items-center gap-2">
              {labelDoors.length > 0 && (
                <>
                  <Button variant="outline" onClick={markPrinted} disabled={busy} data-testid="label-mark-printed">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Mark printed
                  </Button>
                  <Button onClick={() => window.print()} data-testid="label-print">
                    <Printer className="mr-2 h-4 w-4" /> Print
                  </Button>
                </>
              )}
            </div>
          }
        />
        {mode === "building" && (
          <div className="mb-4 flex items-center gap-2">
            <Switch checked={onlyUnprinted} onCheckedChange={setOnlyUnprinted} id="only-unprinted" data-testid="label-only-unprinted" />
            <Label htmlFor="only-unprinted" className="text-sm text-slate-600">Only doors without a printed label</Label>
          </div>
        )}
      </div>

      {loading && <LoadingState label="Preparing labels…" rows={3} />}
      {!loading && labelDoors.length === 0 && (
        <EmptyState title="No labels to print" message={mode === "building" ? "No doors match — try turning off the filter." : "Door not found."} />
      )}
      {!loading && labelDoors.length > 0 && (
        <div className="label-sheet flex flex-wrap gap-4" data-testid="label-grid">
          {labelDoors.map((d) => <LabelCard key={d.id} door={d} buildingName={buildingName} tpl={template} />)}
        </div>
      )}
    </div>
  );
}
